/**
 * Flusso SSO OIDC Authorization Code + PKCE (TD-004 — login SSO di sviluppo).
 *
 * `startLogin()` avvia il redirect verso il provider OIDC (es. oidc-mock);
 * `handleCallback()` completa lo scambio code → token sulla pagina /auth/callback.
 *
 * Il provider mock restituisce un access_token OPAQUE (non verificabile dai servizi
 * Node), quindi usiamo l'**id_token** (JWS RS256, verificabile via JWKS) come Bearer
 * verso le API — vedi packages/auth/src/jwt.ts (createHttpAuth).
 */

import { setToken, getRefreshToken, setRefreshToken, setAuthMode } from './tokenStore.js';
import { decodeJwtPayload } from './jwtDecode.js';
import { sha256 } from './sha256.js';
import { OIDC_CLIENT_ID, authorizeEndpoint, tokenEndpoint, redirectUri } from './oidcConfig.js';
import type { JwtClaims } from './types.js';

// Chiavi sessionStorage usate per conservare verifier/state tra redirect e callback.
// sessionStorage (non localStorage): il flusso PKCE riguarda un singolo tab/redirect.
const PKCE_VERIFIER_KEY = 'tenaciousc_oidc_pkce_verifier';
const STATE_KEY = 'tenaciousc_oidc_state';

/** Genera una stringa casuale URL-safe (usata per `state` e PKCE verifier). */
function randomUrlSafeString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** Codifica un buffer in base64url (RFC 7636 §A). */
function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Calcola il code_challenge S256 = BASE64URL(SHA-256(code_verifier)).
 *
 * `crypto.subtle` esiste solo in *secure context* (HTTPS o localhost/127.0.0.1).
 * Aprendo l'app via un dominio custom in HTTP semplice (es. overlay netbird/tailscale
 * per dev remoto) il browser non lo espone: in quel caso usiamo lo SHA-256 in puro JS
 * (sha256.ts). Il challenge S256 risultante è identico → nessuna modifica lato provider.
 * Su HTTPS/localhost resta la digest nativa (più veloce, constant-time).
 */
async function pkceChallengeS256(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = crypto.subtle
    ? new Uint8Array(await crypto.subtle.digest('SHA-256', data))
    : sha256(data);
  return base64UrlEncode(digest);
}

/**
 * Avvia il login SSO: genera PKCE verifier + challenge e `state`, li salva in
 * sessionStorage e reindirizza il browser all'endpoint /oauth2/authorize del
 * provider OIDC configurato.
 *
 * @param issuer - issuer OIDC (es. VITE_OIDC_ISSUER, già verificato presente dal chiamante)
 */
export async function startLogin(issuer: string): Promise<void> {
  const verifier = randomUrlSafeString(32);
  const challenge = await pkceChallengeS256(verifier);
  const state = randomUrlSafeString(16);

  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: OIDC_CLIENT_ID,
    redirect_uri: redirectUri(),
    scope: 'openid email profile',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  window.location.assign(`${authorizeEndpoint(issuer)}?${params.toString()}`);
}

/** Errore sollevato da handleCallback in caso di state mismatch o scambio fallito. */
export class OidcCallbackError extends Error {}

/** Forma minima della risposta dell'endpoint /oauth2/token che ci interessa. */
interface TokenResponse {
  id_token?: string;
  refresh_token?: string;
  error?: string;
  error_description?: string;
}

/**
 * Completa il flusso di callback: legge `code` e `state` dalla query string corrente,
 * verifica lo `state` rispetto a quello salvato da startLogin, scambia il code (+ PKCE
 * verifier) per i token, salva l'**id_token** come token applicativo e restituisce i
 * claim decodificati.
 *
 * @param issuer       - issuer OIDC
 * @param search       - query string corrente (es. window.location.search)
 * @throws OidcCallbackError se lo state non corrisponde o lo scambio fallisce
 */
export async function handleCallback(issuer: string, search: string): Promise<JwtClaims> {
  const params = new URLSearchParams(search);
  const code = params.get('code');
  const returnedState = params.get('state');
  const errorParam = params.get('error');

  if (errorParam) {
    throw new OidcCallbackError(`Provider OIDC ha restituito un errore: ${errorParam}`);
  }

  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);

  // Pulizia immediata: i valori sessionStorage sono one-shot.
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);

  if (!code) {
    throw new OidcCallbackError('Parametro "code" mancante nella callback OIDC.');
  }
  if (!expectedState || !returnedState || returnedState !== expectedState) {
    throw new OidcCallbackError('Parametro "state" non corrispondente: possibile CSRF.');
  }
  if (!verifier) {
    throw new OidcCallbackError('PKCE verifier mancante in sessionStorage.');
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri(),
    client_id: OIDC_CLIENT_ID,
    code_verifier: verifier,
  });

  const response = await fetch(tokenEndpoint(issuer), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  const data = (await response.json()) as TokenResponse;

  if (!response.ok || !data.id_token) {
    const detail = data.error_description ?? data.error ?? `HTTP ${response.status}`;
    throw new OidcCallbackError(`Scambio token OIDC fallito: ${detail}`);
  }

  // refresh_token (se emesso) per rinnovare la sessione prima di `exp`; null rimuove un
  // eventuale refresh token residuo di una sessione precedente.
  setRefreshToken(data.refresh_token ?? null);
  setAuthMode(null);
  setToken(data.id_token);

  const claims = decodeJwtPayload(data.id_token);
  if (!claims) {
    throw new OidcCallbackError('id_token ricevuto non decodificabile.');
  }

  return claims;
}

// Refresh in corso (single-flight): più 401 concorrenti o timer + 401 condividono la stessa
// chiamata al token endpoint invece di consumare il refresh token N volte.
let inflightRefresh: Promise<string | null> | null = null;

/**
 * Rinnova la sessione con il grant `refresh_token`: salva il nuovo id_token (e l'eventuale
 * refresh token ruotato dall'IdP) e lo restituisce. Restituisce null se non c'è un refresh
 * token (login manuale, IdP che non lo emette) o se il rinnovo fallisce: in quel caso il
 * chiamante decide se fare logout. Non lancia mai.
 *
 * @param issuer - issuer OIDC
 */
export function refreshSession(issuer: string): Promise<string | null> {
  inflightRefresh ??= doRefresh(issuer).finally(() => {
    inflightRefresh = null;
  });
  return inflightRefresh;
}

async function doRefresh(issuer: string): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const response = await fetch(tokenEndpoint(issuer), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: OIDC_CLIENT_ID,
      }).toString(),
    });
    const data = (await response.json()) as TokenResponse;
    if (!response.ok || !data.id_token || !decodeJwtPayload(data.id_token)) return null;

    if (data.refresh_token) setRefreshToken(data.refresh_token);
    setToken(data.id_token);
    return data.id_token;
  } catch {
    // Rete/IdP non raggiungibile: nessun logout qui, decide il chiamante.
    return null;
  }
}
